import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Pencil, Search, Ban, CheckCircle2, MailPlus, Download, ChevronDown, ChevronUp, ShieldCheck, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/format";

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUT_A_VALIDER = "PAIEMENT REÇU – EN ATTENTE DE VALIDATION";
const CATEGORIE_LABELS = { ecole_de_velo: "École de vélo", club: "Licencié club" };
const eurCents = (c) => `${(c / 100).toFixed(2).replace(".", ",")} €`;

const STATUT_STYLES = {
  "INSCRIPTION EN COURS": "bg-steel/15 text-steel",
  "CATÉGORIE À VALIDER": "bg-amber-100 text-amber-700",
  "ÉCOLE DE VÉLO VALIDÉE": "bg-blue-100 text-blue-700",
  "LICENCIÉ CLUB VALIDÉ": "bg-blue-100 text-blue-700",
  "PAIEMENT EN ATTENTE": "bg-steel/15 text-steel",
  [STATUT_A_VALIDER]: "bg-amber-100 text-amber-700",
  "INSCRIPTION VALIDÉE": "bg-green-100 text-green-700",
  "INSCRIPTION REFUSÉE": "bg-rouge/10 text-rouge",
  "DOSSIER À VÉRIFIER": "bg-amber-100 text-amber-700",
  "ADHÉSION EXPIRÉE": "bg-rouge/10 text-rouge",
  "PAIEMENT ÉCHOUÉ": "bg-rouge/10 text-rouge",
  "PAIEMENT ANNULÉ": "bg-amber-100 text-amber-700",
  "PAIEMENT REMBOURSÉ": "bg-navy/10 text-navy",
};

const PAIEMENT_LABELS = {
  pending: "En attente",
  paid: "Réussi",
  failed: "Échoué",
  cancelled: "Annulé",
  refunded: "Remboursé",
  "validation club": "Validation club",
};

const PAIEMENT_STYLES = {
  pending: "bg-steel/15 text-steel",
  paid: "bg-green-100 text-green-700",
  failed: "bg-rouge/10 text-rouge",
  cancelled: "bg-amber-100 text-amber-700",
  refunded: "bg-navy/10 text-navy",
  "validation club": "bg-amber-100 text-amber-700",
};

const FIELDS = [
  ["nom", "Nom"], ["prenom", "Prénom"], ["email", "Email"], ["telephone", "Téléphone"],
  ["date_naissance", "Date de naissance"], ["adresse", "Adresse"], ["code_postal", "Code postal"], ["ville", "Ville"],
];

const eur = (cents) => `${(cents / 100).toFixed(2)} €`.replace(".", ",");

export default function AdminMembres() {
  const [searchParams] = useSearchParams();
  const [membres, setMembres] = useState([]);
  const [stats, setStats] = useState(null);
  const [tarifs, setTarifs] = useState(null);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [licence, setLicence] = useState("");
  const [paiement, setPaiement] = useState("");
  const [saison, setSaison] = useState("2027");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(searchParams.get("inscription"));
  const [testsFilter, setTestsFilter] = useState("");
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [purging, setPurging] = useState(false);
  const [notifEmail, setNotifEmail] = useState("");
  const [recusFiscaux, setRecusFiscaux] = useState(false);
  const [journal, setJournal] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (licence) params.set("licence", licence);
      if (paiement) params.set("paiement", paiement);
      if (saison) params.set("saison", saison);
      if (testsFilter) params.set("tests", testsFilter);
      const { data } = await api.get(`/admin/membres?${params.toString()}`);
      setMembres(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const loadStats = async () => {
    try {
      const [{ data: s }, { data: t }] = await Promise.all([api.get("/admin/membres/stats"), api.get("/admin/tarifs")]);
      setStats(s);
      setTarifs(t);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { loadStats(); loadSettings(); }, []);
  useEffect(() => { load(); }, [licence, paiement, saison, testsFilter]);

  const loadSettings = async () => {
    try {
      const [{ data: st }, { data: j }] = await Promise.all([api.get("/admin/settings"), api.get("/admin/journal")]);
      setNotifEmail(st.email_notifications || "");
      setRecusFiscaux(Boolean(st.recus_fiscaux_actifs));
      setJournal(j);
    } catch { /* non bloquant */ }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", { email_notifications: notifEmail, recus_fiscaux_actifs: recusFiscaux });
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const purgerTests = async () => {
    setPurging(true);
    try {
      const { data } = await api.delete("/admin/adhesions/tests");
      toast.success(`${data.inscriptions} inscription(s) de test supprimée(s), ${data.comptes} compte(s) de test supprimé(s)`);
      setConfirmPurge(false);
      load();
      loadSettings();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPurging(false);
    }
  };

  const aValider = membres
    .filter((m) => m.statut === STATUT_A_VALIDER)
    .sort((a, b) => (b.paid_at || "").localeCompare(a.paid_at || ""));

  const catAValider = membres
    .filter((m) => m.statut === "CATÉGORIE À VALIDER")
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  const [catChoix, setCatChoix] = useState({});

  const validerCategorie = async (m) => {
    const choix = catChoix[m.id];
    if (!choix) {
      toast.error("Sélectionnez École de vélo ou Licencié club");
      return;
    }
    try {
      const { data } = await api.post(`/admin/membres/${m.id}/categorie`, { categorie: choix });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const valider = async (m) => {
    if (!window.confirm(`Valider l'inscription de ${m.prenom} ${m.nom} (${m.total_label}) ?`)) return;
    try {
      const { data } = await api.post(`/admin/membres/${m.id}/valider`);
      toast.success(data.message);
      load();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const refuser = async (m) => {
    const motif = window.prompt(`Refuser l'inscription de ${m.prenom} ${m.nom} ?\nMotif du refus (optionnel) :`, "");
    if (motif === null) return;
    try {
      const { data } = await api.post(`/admin/membres/${m.id}/refuser`, { motif });
      toast.success(data.message);
      load();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const supprimerDossier = async (m) => {
    const paye = m.payment_status === "paid";
    const ok = window.confirm(
      `Supprimer définitivement le dossier de ${m.prenom} ${m.nom} (${m.email}) ?\n\n` +
      (paye ? "⚠️ Ce dossier est PAYÉ — le remboursement reste à gérer dans Stripe si besoin.\n\n" : "") +
      "La fiche du licencié est conservée si elle a un historique. Le compte de connexion n'est pas supprimé."
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/admin/membres/${m.id}`);
      toast.success(data.message + (data.fiche_supprimee ? " (fiche licencié retirée)" : " (fiche licencié conservée)"));
      setDetail(null);
      setEditing(null);
      load();
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startEdit = (m) => {
    setEditing(m.id);
    setForm(Object.fromEntries(FIELDS.map(([k]) => [k, m[k] || ""])));
    window.scrollTo({ top: 0 });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/membres/${editing}`, form);
      toast.success("Inscription mise à jour");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleCompte = async (m) => {
    const actif = m.compte?.actif;
    if (actif && !window.confirm(`Désactiver le compte de ${m.prenom} ${m.nom} ? Il ne pourra plus se connecter.`)) return;
    try {
      const { data } = await api.patch(`/admin/membres/${m.id}/compte`, { active: !actif });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const renvoyerAcces = async (m) => {
    try {
      const { data } = await api.post(`/admin/membres/${m.id}/renvoyer-acces`);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exporter = (format) => window.open(`${API_BASE}/api/admin/membres/export${format === "xlsx" ? "?format=xlsx" : ""}`, "_blank");

  return (
    <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
      {stats && tarifs && (
        <div className="mb-10" data-testid="inscriptions-stats">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { label: `Adhérents ${stats.saison}`, value: stats.adherents, testId: "stats-adherents" },
              { label: "Total encaissé", value: eur(stats.total_encaisse_cents), testId: "stats-total" },
              { label: "Paiements à valider", value: stats.a_valider, testId: "stats-a-valider" },
              { label: "Paiements en attente", value: stats.en_attente, testId: "stats-attente" },
              { label: "Inscriptions totales", value: stats.inscriptions, testId: "stats-inscriptions" },
            ].map((s) => (
              <div key={s.testId} className={`p-6 ${s.testId === "stats-a-valider" && s.value > 0 ? "bg-amber-500 text-white" : "bg-navy text-white"}`} data-testid={s.testId}>
                <p className="font-heading font-semibold text-3xl">{s.value}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-white/70">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="stats-licences">
            {tarifs.ffc.map((f) => (
              <span key={f.id} data-testid={`stats-ffc-${f.id}`} className="border border-navy/15 bg-white px-3.5 py-2 text-xs font-heading uppercase tracking-wider text-navy">
                {f.label.replace("FFC ", "")} <strong className="text-rouge">{stats.ffc[f.id] || 0}</strong>
              </span>
            ))}
            <span data-testid="stats-fsgt" className="border border-navy/15 bg-white px-3.5 py-2 text-xs font-heading uppercase tracking-wider text-navy">
              FSGT <strong className="text-rouge">{stats.fsgt}</strong>
            </span>
          </div>
        </div>
      )}

      {catAValider.length > 0 && (
        <div className="mb-10 border-2 border-blue-300 bg-blue-50" data-testid="categories-a-valider">
          <div className="bg-navy text-white px-6 py-4 flex items-center gap-3">
            <ShieldCheck size={18} />
            <h2 className="font-heading uppercase font-semibold tracking-wider text-sm">
              Catégories à valider ({catAValider.length})
            </h2>
          </div>
          <div>
            {catAValider.map((m) => (
              <div key={m.id} className="px-6 py-5 border-b border-blue-200 last:border-0" data-testid={`cat-a-valider-${m.id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-heading uppercase font-semibold text-navy">
                      {m.prenom} {m.nom}
                      {m.is_test && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-steel/15 text-steel">TEST</span>}
                    </p>
                    <p className="text-xs text-steel">
                      {m.email} · né(e) le {m.date_naissance} · {m.mineur ? "mineur" : "majeur"} · dossier déposé le {fmtDate(m.created_at)}
                    </p>
                    {m.categorie_proposee && (
                      <p className="mt-1.5 text-xs" data-testid={`cat-proposee-${m.id.slice(0, 8)}`}>
                        <span className="font-semibold text-navy">
                          Proposé (âge au 31/12/{m.saison || "2027"}{m.age_31_12 !== null && m.age_31_12 !== undefined ? ` : ${m.age_31_12} ans` : ""}) →{" "}
                          {m.categorie_proposee === "ecole_de_velo" ? "ÉCOLE DE VÉLO" : "LICENCIÉ CLUB"}
                        </span>
                        {m.age_31_12 !== null && m.age_31_12 !== undefined && m.age_31_12 < 2 && (
                          <span className="ml-2 text-amber-700">— hors conditions du Pack École de vélo</span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {[["ecole_de_velo", "École de vélo"], ["club", "Licencié club"]].map(([v, label]) => (
                      <label key={v} className={`flex items-center gap-2 border px-4 py-2.5 cursor-pointer text-xs font-heading uppercase tracking-wider transition-[border-color,background-color] duration-300 ${catChoix[m.id] === v ? "border-rouge bg-rouge/5 text-rouge" : "border-navy/20 text-navy/70 hover:border-rouge"}`} data-testid={`cat-choix-${v}-${m.id.slice(0, 8)}`}>
                        <input
                          type="radio"
                          name={`cat-${m.id}`}
                          checked={catChoix[m.id] === v}
                          onChange={() => setCatChoix({ ...catChoix, [m.id]: v })}
                          className="h-3.5 w-3.5 accent-[#D90429]"
                        />
                        {label}
                        {m.categorie_proposee === v && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-blue-600 text-white">suggéré</span>
                        )}
                      </label>
                    ))}
                    <button
                      onClick={() => validerCategorie(m)}
                      data-testid={`cat-valider-${m.id.slice(0, 8)}`}
                      className="inline-flex items-center gap-2 bg-green-600 text-white font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-navy transition-colors duration-300"
                    >
                      <CheckCircle2 size={14} /> Valider la catégorie
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {aValider.length > 0 && (
        <div className="mb-10 border-2 border-amber-400 bg-amber-50" data-testid="paiements-a-valider">
          <div className="bg-amber-500 text-white px-6 py-4 flex items-center gap-3">
            <ShieldCheck size={18} />
            <h2 className="font-heading uppercase font-semibold tracking-wider text-sm">
              Paiements à valider ({aValider.length})
            </h2>
          </div>
          <div>
            {aValider.map((m) => (
              <div key={m.id} className="px-6 py-5 border-b border-amber-200 last:border-0 flex flex-wrap items-center gap-4" data-testid={`a-valider-${m.id.slice(0, 8)}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-heading uppercase font-semibold text-navy">{m.prenom} {m.nom}</p>
                  <p className="text-xs text-steel">
                    {m.licence_label} · payé le {(m.paid_at || "").slice(0, 10)} · <span className="font-semibold text-amber-700 uppercase tracking-wider text-[10px]">Paiement reçu – à valider</span>
                  </p>
                </div>
                <p className="font-heading font-semibold text-navy text-lg">{m.total_label}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => valider(m)}
                    data-testid={`valider-${m.id.slice(0, 8)}`}
                    className="inline-flex items-center gap-2 bg-green-600 text-white font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-navy transition-colors duration-300"
                  >
                    <CheckCircle2 size={14} /> Valider l'inscription
                  </button>
                  <button
                    onClick={() => refuser(m)}
                    data-testid={`refuser-${m.id.slice(0, 8)}`}
                    className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-navy transition-colors duration-300"
                  >
                    <XCircle size={14} /> Refuser
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-1 min-w-60">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-steel" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="membres-search"
              placeholder="Rechercher un adhérent…"
              className="w-full border border-navy/20 bg-white pl-10 pr-4 py-2.5 text-sm text-navy focus:outline-none focus:border-rouge"
            />
          </div>
          <button type="submit" data-testid="membres-search-submit" className="bg-navy text-white font-heading uppercase tracking-wider text-xs px-5 hover:bg-rouge transition-colors duration-300">
            Rechercher
          </button>
        </form>
        <select value={saison} onChange={(e) => setSaison(e.target.value)} data-testid="membres-filter-saison" className={`${inputCls} !w-auto !py-2.5 text-xs`}>
          <option value="2027">Saison 2027</option>
          <option value="">Toutes saisons</option>
        </select>
        <select value={licence} onChange={(e) => setLicence(e.target.value)} data-testid="membres-filter-licence" className={`${inputCls} !w-auto !py-2.5 text-xs`}>
          <option value="">Toutes licences</option>
          {(tarifs?.ffc || []).map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
          <option value="fsgt">Licence FSGT</option>
          <option value="aucune">Sans licence</option>
        </select>
        <select value={paiement} onChange={(e) => setPaiement(e.target.value)} data-testid="membres-filter-paiement" className={`${inputCls} !w-auto !py-2.5 text-xs`}>
          <option value="">Tous paiements</option>
          <option value="paid">Réussi</option>
          <option value="pending">En attente</option>
          <option value="failed">Échoué</option>
          <option value="cancelled">Annulé</option>
          <option value="refunded">Remboursé</option>
        </select>
        <div className="flex items-center gap-1 border border-navy/20 bg-white p-1" data-testid="membres-filter-tests">
          {[["", "Toutes"], ["0", "Réelles"], ["1", "Tests"]].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setTestsFilter(v)}
              data-testid={`membres-tests-${v === "" ? "toutes" : v === "0" ? "reelles" : "tests"}`}
              className={`font-heading uppercase tracking-wider text-[11px] px-3.5 py-2 transition-[background-color,color] duration-300 ${testsFilter === v ? "bg-navy text-white" : "text-navy/60 hover:text-rouge"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button onClick={() => exporter("csv")} data-testid="membres-export-csv" className="inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
          <Download size={14} /> CSV
        </button>
        <button onClick={() => exporter("xlsx")} data-testid="membres-export-xlsx" className="inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
          <Download size={14} /> Excel
        </button>
        <button onClick={() => setConfirmPurge(true)} data-testid="membres-purge-tests" className="inline-flex items-center gap-2 border border-rouge/40 text-rouge font-heading uppercase tracking-wider text-xs px-4 py-2.5 hover:bg-rouge hover:text-white transition-[background-color,color] duration-300">
          <Ban size={14} /> Supprimer les inscriptions de test
        </button>
      </div>

      <form onSubmit={saveSettings} className="flex flex-wrap items-center gap-3 mb-8" data-testid="notif-email-form">
        <label htmlFor="notif-email" className="font-heading uppercase tracking-wider text-xs text-steel">Adresse e-mail pour les notifications :</label>
        <input
          id="notif-email"
          type="email"
          required
          value={notifEmail}
          onChange={(e) => setNotifEmail(e.target.value)}
          data-testid="notif-email-input"
          className={`${inputCls} !w-72 !py-2.5`}
        />
        <button type="submit" data-testid="notif-email-save" className="bg-navy text-white font-heading uppercase tracking-wider text-xs px-5 py-2.5 hover:bg-rouge transition-colors duration-300">
          Enregistrer
        </button>
        <label className="flex items-center gap-2.5 text-xs text-navy/80 cursor-pointer" data-testid="recus-fiscaux-label">
          <input
            type="checkbox"
            checked={recusFiscaux}
            onChange={(e) => setRecusFiscaux(e.target.checked)}
            data-testid="recus-fiscaux-toggle"
            className="h-4 w-4 accent-[#D90429]"
          />
          Club habilité à émettre des reçus fiscaux (désactivé par défaut — n'activer qu'après vérification de l'habilitation)
        </label>
      </form>

      {journal.length > 0 && (
        <p className="text-xs text-steel mb-6" data-testid="admin-journal-last">
          Journal : {journal[0].nb_inscriptions} inscriptions de test supprimées par l'administrateur le {fmtDate(journal[0].created_at)} à {(journal[0].created_at || "").slice(11, 16)}.
        </p>
      )}

      {confirmPurge && (
        <div className="fixed inset-0 z-50 bg-navy/70 flex items-center justify-center p-5" data-testid="purge-modal" onClick={() => setConfirmPurge(false)}>
          <div className="bg-white max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl mb-4">Supprimer les inscriptions de test</h3>
            <p className="text-sm text-navy/80 leading-relaxed">
              Vous êtes sur le point de supprimer définitivement toutes les inscriptions identifiées comme TEST.
              Cette action supprimera uniquement les données de test et ne modifiera pas les inscriptions réelles.
              Confirmer la suppression ?
            </p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirmPurge(false)} data-testid="purge-cancel" className="flex-1 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-5 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                Annuler
              </button>
              <button onClick={purgerTests} disabled={purging} data-testid="purge-confirm" className="flex-1 inline-flex items-center justify-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-xs px-5 py-3.5 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
                {purging && <Loader2 size={14} className="animate-spin" />}
                Supprimer les tests
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-steel text-sm mb-6" data-testid="membres-count">{membres.length} inscription{membres.length > 1 ? "s" : ""}</p>

      {editing && form && (
        <form onSubmit={save} data-testid="membre-editor" className="bg-white border border-navy/10 p-6 sm:p-8 mb-10">
          <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl mb-6">Modifier l'inscription</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {FIELDS.map(([k, label]) => (
              <div key={k} className={k === "adresse" ? "sm:col-span-2" : ""}>
                <label className={labelCls} htmlFor={`m-${k}`}>{label}</label>
                <input id={`m-${k}`} data-testid={`membre-field-${k}`} type={k === "email" ? "email" : k === "date_naissance" ? "date" : "text"} required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} />
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <button type="submit" disabled={saving} data-testid="membre-save" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
              {saving && <Loader2 size={15} className="animate-spin" />}
              Enregistrer
            </button>
            <button type="button" onClick={() => setEditing(null)} data-testid="membre-cancel" className="border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
              Annuler
            </button>
          </div>
        </form>
      )}

      {busy ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div>
      ) : membres.length === 0 ? (
        <div className="border border-dashed border-navy/25 px-8 py-16 text-center" data-testid="membres-empty">
          <p className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Aucune inscription</p>
          <p className="mt-2 text-sm text-steel">Les inscriptions en ligne de la saison 2027 apparaîtront ici.</p>
        </div>
      ) : (
        <div className="bg-white border border-navy/10" data-testid="membres-table">
          {membres.map((m) => (
            <div key={m.id} className="px-6 py-5 border-b border-navy/10 last:border-0" data-testid={`membre-row-${m.id.slice(0, 8)}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className={`shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 ${STATUT_STYLES[m.statut] || "bg-steel/15 text-steel"}`}>
                  {m.statut}
                </span>
                <span className={`shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 ${PAIEMENT_STYLES[m.payment_status] || "bg-steel/15 text-steel"}`} data-testid={`membre-paiement-${m.id.slice(0, 8)}`}>
                  {PAIEMENT_LABELS[m.payment_status] || m.payment_status}
                </span>
                <span
                  className={`shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 ${m.compte?.existe ? (m.compte.actif ? "bg-navy text-white" : "bg-rouge/10 text-rouge") : "bg-mist text-steel border border-navy/15"}`}
                  data-testid={`membre-compte-${m.id.slice(0, 8)}`}
                >
                  {m.compte?.existe ? (m.compte.actif ? "Compte actif" : "Compte désactivé") : "Pas de compte"}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-heading uppercase font-semibold text-navy">
                    {m.prenom} {m.nom}
                    {m.is_test && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-steel/15 text-steel" data-testid={`membre-test-${m.id.slice(0, 8)}`}>TEST</span>}
                  </p>
                  <p className="text-xs text-steel">
                    {m.email} · {m.telephone} · né(e) le {m.date_naissance} · inscrit le {fmtDate(m.created_at)}{m.saison ? ` · saison ${m.saison}` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-heading uppercase font-semibold text-navy text-sm">{m.licence_label}</p>
                  <p className="text-xs text-rouge font-semibold">{m.total_label}</p>
                </div>
                <div className="flex items-center gap-1">
                  {m.statut === STATUT_A_VALIDER && (
                    <>
                      <button onClick={() => valider(m)} data-testid={`membre-valider-${m.id.slice(0, 8)}`} className="p-2 text-green-600 hover:text-navy transition-colors" title="Valider l'inscription" aria-label="Valider l'inscription">
                        <CheckCircle2 size={17} />
                      </button>
                      <button onClick={() => refuser(m)} data-testid={`membre-refuser-${m.id.slice(0, 8)}`} className="p-2 text-rouge hover:text-navy transition-colors" title="Refuser l'inscription" aria-label="Refuser l'inscription">
                        <XCircle size={17} />
                      </button>
                    </>
                  )}
                  <button onClick={() => setDetail(detail === m.id ? null : m.id)} data-testid={`membre-detail-${m.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" title="Détail" aria-label="Voir le détail">
                    {detail === m.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  <button onClick={() => startEdit(m)} data-testid={`membre-edit-${m.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" title="Modifier" aria-label="Modifier">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => renvoyerAcces(m)} data-testid={`membre-resend-${m.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" title="Renvoyer l'email d'accès" aria-label="Renvoyer l'email d'accès">
                    <MailPlus size={16} />
                  </button>
                  {m.compte?.existe && (
                    <button onClick={() => toggleCompte(m)} data-testid={`membre-toggle-${m.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" title={m.compte.actif ? "Désactiver le compte" : "Réactiver le compte"} aria-label="Activer / désactiver le compte">
                      {m.compte.actif ? <Ban size={16} /> : <CheckCircle2 size={16} />}
                    </button>
                  )}
                </div>
              </div>
              {detail === m.id && (
                <div className="mt-4 border border-navy/10 bg-mist/40 p-5 text-sm" data-testid={`membre-detail-box-${m.id.slice(0, 8)}`}>
                  {m.payment_status !== "paid" && (
                    <div className="mb-5" data-testid={`membre-categorie-${m.id.slice(0, 8)}`}>
                      <p className="font-heading uppercase tracking-wider text-xs text-steel mb-3">Catégorie {m.saison || "2027"}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        {[["ecole_de_velo", "École de vélo"], ["club", "Licencié club"]].map(([v, label]) => (
                          <label key={v} className={`flex items-center gap-2 border px-4 py-2.5 cursor-pointer text-xs font-heading uppercase tracking-wider bg-white transition-[border-color,background-color] duration-300 ${(catChoix[m.id] || m.categorie_2027) === v ? "border-rouge bg-rouge/5 text-rouge" : "border-navy/20 text-navy/70 hover:border-rouge"}`} data-testid={`detail-cat-${v}-${m.id.slice(0, 8)}`}>
                            <input
                              type="radio"
                              name={`cat-detail-${m.id}`}
                              checked={(catChoix[m.id] || m.categorie_2027) === v}
                              onChange={() => setCatChoix({ ...catChoix, [m.id]: v })}
                              className="h-3.5 w-3.5 accent-[#D90429]"
                            />
                            {label}
                          </label>
                        ))}
                        <button
                          onClick={() => validerCategorie(m)}
                          data-testid={`detail-cat-valider-${m.id.slice(0, 8)}`}
                          className="inline-flex items-center gap-2 bg-green-600 text-white font-heading uppercase tracking-wider text-xs px-5 py-2.5 hover:bg-navy transition-colors duration-300"
                        >
                          <CheckCircle2 size={14} /> Valider la catégorie
                        </button>
                      </div>
                      {(m.categorie_history || []).length > 0 && (
                        <div className="mt-3 space-y-1" data-testid={`cat-history-${m.id.slice(0, 8)}`}>
                          {m.categorie_history.map((h, i) => (
                            <p key={i} className="text-[11px] text-steel">
                              {h.ancienne ? `${h.ancienne === "ecole_de_velo" ? "École de vélo" : "Licencié club"} → ` : ""}
                              {h.nouvelle === "ecole_de_velo" ? "École de vélo" : "Licencié club"} — par {h.admin} le {(h.date || "").slice(0, 16).replace("T", " à ")}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {m.statut === STATUT_A_VALIDER && (
                    <div className="mb-5 flex flex-wrap gap-3">
                      <button
                        onClick={() => valider(m)}
                        data-testid={`detail-valider-${m.id.slice(0, 8)}`}
                        className="inline-flex items-center gap-2 bg-green-600 text-white font-heading uppercase tracking-wider text-xs px-6 py-3.5 hover:bg-navy transition-colors duration-300"
                      >
                        <CheckCircle2 size={15} /> Valider l'inscription
                      </button>
                      <button
                        onClick={() => refuser(m)}
                        data-testid={`detail-refuser-${m.id.slice(0, 8)}`}
                        className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-xs px-6 py-3.5 hover:bg-navy transition-colors duration-300"
                      >
                        <XCircle size={15} /> Refuser l'inscription
                      </button>
                    </div>
                  )}
                  {m.statut === "INSCRIPTION VALIDÉE" && m.validated_at && (
                    <p className="mb-4 text-xs text-steel">Validée le {m.validated_at.slice(0, 16).replace("T", " à ")} par {m.validated_by}</p>
                  )}
                  {m.statut === "INSCRIPTION REFUSÉE" && (
                    <p className="mb-4 text-xs text-rouge">
                      Refusée le {(m.refused_at || "").slice(0, 16).replace("T", " à ")} par {m.refused_by}{m.motif_refus ? ` — Motif : ${m.motif_refus}` : ""}.
                      Le paiement est conservé ; le remboursement se traite depuis le tableau de bord Stripe.
                    </p>
                  )}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <p className="font-heading uppercase tracking-wider text-xs text-steel mb-3">Sélection du licencié — saison {m.saison || "2027"}</p>
                      <div className="space-y-1.5 mb-5 bg-mist border border-navy/10 p-4" data-testid={`detail-selection-${m.id.slice(0, 8)}`}>
                        <p className="flex justify-between gap-4"><span className="text-steel">Licencié</span><span className="font-semibold text-navy">{m.prenom} {m.nom}</span></p>
                        <p className="flex justify-between gap-4"><span className="text-steel">Catégorie</span><span className="font-semibold text-navy">{CATEGORIE_LABELS[m.categorie_2027] || "À valider"}</span></p>
                        {m.montants?.pack_cents > 0 && (
                          <p className="flex justify-between gap-4"><span className="text-steel">Pack École de vélo</span><span className="font-semibold text-navy">{eurCents(m.montants.pack_cents)}</span></p>
                        )}
                        <p className="flex justify-between gap-4">
                          <span className="text-steel">Licence FSGT</span>
                          <span className={`font-semibold ${m.fsgt_requested || m.fsgt ? "text-navy" : "text-steel"}`} data-testid={`detail-fsgt-${m.id.slice(0, 8)}`}>{m.fsgt_requested || m.fsgt ? "OUI" : "NON"}</span>
                        </p>
                        {(m.fsgt_requested || m.fsgt) && m.montants?.fsgt_cents > 0 && (
                          <p className="flex justify-between gap-4">
                            <span className="text-steel">Tarif FSGT appliqué</span>
                            <span className="font-semibold text-navy">{eurCents(m.montants.fsgt_cents)} <span className="text-steel font-normal text-xs">({m.categorie_2027 === "ecole_de_velo" ? "École de vélo" : "adulte"})</span></span>
                          </p>
                        )}
                        <p className="flex justify-between gap-4 pt-2 border-t border-navy/10"><span className="text-navy font-semibold">Total payé</span><span className="font-semibold text-rouge">{m.payment_status === "paid" ? m.total_label : "—"}</span></p>
                      </div>
                      <p className="font-heading uppercase tracking-wider text-xs text-steel mb-3">Détail du montant</p>
                      <div className="space-y-1.5">
                        {(m.lignes || []).map((l, i) => (
                          <p key={i} className="flex justify-between gap-4"><span className="text-steel">{l.label}</span><span className="font-semibold text-navy">{l.montant}</span></p>
                        ))}
                        <p className="flex justify-between gap-4 pt-2 border-t border-navy/10"><span className="text-navy font-semibold">Total</span><span className="font-semibold text-rouge">{m.total_label}</span></p>
                      </div>
                      {m.equipements?.length > 0 && (
                        <p className="mt-3 text-steel border-l-2 border-navy/15 pl-4">
                          Équipements : {m.equipements.map((e) => `${e.label} ${e.taille} ×${e.quantite}`).join(" · ")}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="font-heading uppercase tracking-wider text-xs text-steel mb-3">Paiement</p>
                      <div className="space-y-1.5">
                        <p className="flex justify-between gap-4"><span className="text-steel">Statut</span><span className="font-semibold text-navy">{PAIEMENT_LABELS[m.payment_status] || m.payment_status}</span></p>
                        <p className="flex justify-between gap-4"><span className="text-steel">Payé le</span><span className="font-semibold text-navy">{(m.paid_at || "—").slice(0, 10)}</span></p>
                        <p className="flex justify-between gap-4"><span className="text-steel">Transaction Stripe</span><span className="font-mono text-xs text-navy break-all text-right">{m.session_id || "—"}</span></p>
                      </div>
                    </div>
                  </div>
                  {(m.documents_acceptes || []).length > 0 && (
                    <div className="mt-5" data-testid={`membre-documents-${m.id.slice(0, 8)}`}>
                      <p className="font-heading uppercase tracking-wider text-xs text-steel mb-3">
                        Documents validés{m.mineur ? " — licencié mineur (validations du représentant légal)" : ""}
                      </p>
                      <div className="space-y-1.5">
                        {m.documents_acceptes.map((d, i) => (
                          <p key={i} className="text-sm text-navy flex items-start gap-2" data-testid={`membre-doc-${i}`}>
                            <span className="text-green-700 font-bold">✓</span>
                            <span>{d.title} — Version {d.version} <span className="text-steel text-xs">· validé le {(d.valide_le || "").slice(0, 16).replace("T", " à ")}</span></span>
                          </p>
                        ))}
                      </div>
                      {m.fiche_sante && (
                        <p className="mt-2 text-xs text-steel">
                          Fiche santé : responsable {m.fiche_sante.responsable1_nom} ({m.fiche_sante.responsable1_telephone})
                          {m.fiche_sante.urgence_nom ? ` · urgence : ${m.fiche_sante.urgence_nom} (${m.fiche_sante.urgence_telephone})` : ""}
                          {m.fiche_sante.allergies ? ` · allergies : ${m.fiche_sante.allergies}` : ""}
                        </p>
                      )}
                      {m.autorisation_parentale && (
                        <p className="mt-1 text-xs text-steel">
                          Autorisation parentale signée par {m.autorisation_parentale.responsable_nom}
                          {m.autorisation_parentale.responsable_lien ? ` (${m.autorisation_parentale.responsable_lien})` : ""}.
                        </p>
                      )}
                    </div>
                  )}
                  <div className="mt-6 pt-5 border-t border-navy/10 flex justify-end">
                    <button
                      onClick={() => supprimerDossier(m)}
                      data-testid={`detail-supprimer-${m.id.slice(0, 8)}`}
                      className="inline-flex items-center gap-2 border border-rouge/40 text-rouge font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-rouge hover:text-white transition-colors duration-300"
                    >
                      <Trash2 size={14} /> Supprimer ce dossier
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
